import { Component, Input, Output, EventEmitter, ViewChild, OnChanges, OnInit, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatNativeDateModule } from '@angular/material/core';
import { MatCalendar, MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';

export interface DateRangeResult {
  endDate: string | null;
  endDateObj?: Date | null;
  endTime?: string;
  leftCalendarMonth?: Date | null;
  startDate: string | null;
  startDateObj?: Date | null;
  startTime?: string;
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-date-range-picker',
  standalone: true,
  host: {
    '[class.single-date]': 'isSingleDate',
    '[class.align-right]': 'alignRight'
  },
  imports: [
    FormsModule,
    MatButtonModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatNativeDateModule,
    MatSelectModule,
    MatTooltipModule
  ],
  templateUrl: './date-range-picker.component.html',
  styleUrls: ['./date-range-picker.component.scss']
})
export class DateRangePickerComponent implements OnInit, OnChanges {
  @Input() alignRight = false;
  @Input() dataPwPrefix: string | null = null;
  @Input() dataQaPrefix: string | null = null;
  @Input() disabled = false;
  @Input() endDate!: string | null;
  @Input() isSingleDate = false;
  @Input() maxDate: Date | null = null;
  @Input() minDate: Date | null = null;
  @Input() showToolTip = false;
  @Input() startDate!: string | null;
  @Input() timeIntervalMinutes = 30;

  /**
   * If false, hides the time selection fields (time box) everywhere.
   */
  @Input() showTime: boolean = true;

  @Output() cancelled = new EventEmitter<void>();
  @Output() endDateChange = new EventEmitter<string | null>();
  @Output() rangeChange = new EventEmitter<DateRangeResult>();
  @Output() startDateChange = new EventEmitter<string | null>();

  @ViewChild('endCalendar') endCalendar!: MatCalendar<Date>;
  @ViewChild('startCalendar') startCalendar!: MatCalendar<Date>;

  displayDateValue: string = '';
  displayTimeValue: string = '';
  endDateObj: Date | null = null;
  endTime: string | null = null;
  leftCalendarMonth: Date = new Date();
  showPicker: boolean = false;
  snapshot: DateRangeResult | null = null;
  startDateObj: Date | null = null;
  startTime: string | null = null;
  timeSlots: string[] = [];

  // Right calendar always shows next month
  get rightCalendarStart(): Date {
    const baseDate = this.endDateObj ?? this.startDateObj ?? new Date();
    const nextMonth = new Date(baseDate); // clone

    if (!this.endDateObj || this.endDateObj.getMonth() === this.startDateObj?.getMonth()) {
      nextMonth.setMonth(nextMonth.getMonth() + 1);
    }

    return nextMonth;
  }

  // Optional: If you want the left calendar to always start at current month initially
  ngOnInit(): void {
    this.generateTimeSlots();
    this.initDateTime();
    this.setDisplayDateTime();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['startDate'] || changes['endDate']) {
      this.initDateTime();
      this.setDisplayDateTime();
    }
  }

  generateTimeSlots(): void {
    const slots: string[] = [];

    for (let totalMinutes = 0; totalMinutes < 24 * 60; totalMinutes += this.timeIntervalMinutes) {
      const hour = Math.floor(totalMinutes / 60);
      const min = totalMinutes % 60;

      const date = new Date();
      date.setHours(hour, min, 0, 0);

      const formatted = date.toLocaleTimeString([], {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });

      slots.push(formatted);
    }

    this.timeSlots = slots;
  }

  initDateTime(): void {
    this.startDateObj = this.startDate ? this.parseFromIsoLocalClock(this.startDate) : null;
    this.endDateObj = this.endDate ? this.parseFromIsoLocalClock(this.endDate) : null;
    this.startTime = this.startDateObj
      ? this.startDateObj.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })
      : null;
    this.endTime = this.endDateObj
      ? this.endDateObj.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })
      : null;
    this.captureSnapshot();
  }

  captureSnapshot(): void {
    if (this.startDateObj) {
      this.leftCalendarMonth = new Date(this.startDateObj.getFullYear(), this.startDateObj.getMonth(), 1);
    } else {
      this.leftCalendarMonth = new Date(); // current month
    }
    this.snapshot = {
      startDate: this.startDate,
      endDate: this.endDate,
      startTime: this.startTime ?? undefined,
      endTime: this.endTime ?? undefined,
      startDateObj: this.startDateObj,
      endDateObj: this.endDateObj,
      leftCalendarMonth: this.leftCalendarMonth
    };
  }

  onDateSelected(date: Date | null): void {
    if (!date) return;

    const selected = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    if (this.isSingleDate) {
      this.startDate = this.parseToIsoLocalCLock(selected);
      this.startDateObj = selected;
      this.endDate = null;
      this.endDateObj = null;
      this.refreshCalendars();
      return;
    }

    if (!this.startDate || (this.startDate && this.endDate)) {
      this.startDate = this.parseToIsoLocalCLock(selected);
      this.startDateObj = selected;
      this.endDate = null;
      this.endDateObj = null;
    } else {
      const start = this.parseFromIsoLocalClock(this.startDate);

      if (selected < start) {
        this.endDate = this.startDate;
        this.endDateObj = this.parseFromIsoLocalClock(this.startDate);

        this.startDate = this.parseToIsoLocalCLock(selected);
        this.startDateObj = selected;
      } else {
        this.endDate = this.parseToIsoLocalCLock(selected);
        this.endDateObj = selected;
      }
    }

    this.refreshCalendars();
  }

  applyDateAndTime(): void {
    const combinedStart = this.combineDateAndTime(this.startDateObj?.toISOString() ?? null, this.startTime);
    const combinedEnd = this.isSingleDate
      ? null
      : this.combineDateAndTime(this.endDateObj?.toISOString() ?? null, this.endTime);

    this.startDate = combinedStart || null;
    this.endDate = this.isSingleDate ? null : combinedEnd;

    this.startDateObj = this.startDate ? this.parseFromIsoLocalClock(this.startDate) : null;
    this.endDateObj = this.endDate ? this.parseFromIsoLocalClock(this.endDate) : null;

    this.startTime = this.startTime || '12:00 am';
    this.endTime = this.endTime || '12:00 am';

    if (!this.validateRange()) {
      return;
    }

    this.setDisplayDateTime();
    this.showPicker = false;
    this.captureSnapshot();

    this.rangeChange.emit({
      startDate: this.startDate,
      endDate: this.endDate,
      startTime: this.startTime ?? undefined,
      endTime: this.endTime ?? undefined,
      startDateObj: this.startDateObj,
      endDateObj: this.endDateObj,
      leftCalendarMonth: this.leftCalendarMonth
    });

    this.startDateChange.emit(this.startDate);
    this.endDateChange.emit(this.endDate);
  }

  cancel(): void {
    this.showPicker = false;
    if (this.snapshot) {
      this.startDate = this.snapshot.startDate;
      this.endDate = this.snapshot.endDate;
      this.startTime = this.snapshot.startTime ?? null;
      this.endTime = this.snapshot.endTime ?? null;
      this.startDateObj = this.snapshot.startDateObj!;
      this.endDateObj = this.snapshot.endDateObj!;
      this.leftCalendarMonth = this.snapshot.leftCalendarMonth!;
    }
    // implement if needed
    this.cancelled.emit();
  }

  // Force Angular Material calendar to re-render
  refreshCalendars(): void {
    if (this.startCalendar) this.startCalendar.updateTodaysDate();
    if (this.endCalendar) this.endCalendar.updateTodaysDate();
  }

  dateClass = (d: Date) => {
    if (!this.startDateObj) return '';

    // Only compare the date part
    const date = new Date(d.getFullYear(), d.getMonth(), d.getDate());

    // normalize start and end to ignore time
    const startDay = new Date(
      this.startDateObj.getFullYear(),
      this.startDateObj.getMonth(),
      this.startDateObj.getDate()
    );
    const endDay = this.endDateObj
      ? new Date(this.endDateObj.getFullYear(), this.endDateObj.getMonth(), this.endDateObj.getDate())
      : startDay;

    if (date.getTime() === startDay.getTime() && !this.isSingleDate) return 'range-start';
    if (date.getTime() === endDay.getTime() && !this.isSingleDate) return 'range-end';
    if (date > startDay && date < endDay && !this.isSingleDate) return 'range-middle';

    return '';
  };

  private setDisplayDateTime(): void {
    if (this.isSingleDate) {
      this.displayDateValue = this.startDateObj ? this.formatDisplayDate(this.startDateObj) : '';
      this.displayTimeValue = this.startTime ?? '';
      return;
    }
    this.displayDateValue =
      this.startDateObj && this.endDateObj
        ? `${this.formatDisplayDate(this.startDateObj)} - ${this.formatDisplayDate(this.endDateObj)}`
        : '';

    this.displayTimeValue =
      this.startTime || this.endTime ? `${this.startTime || '12:00 am '} - ${this.endTime || '12:00 am'}` : '';
  }

  validationMessage: string | null = null;

  private isSameDay(start: Date | null, end: Date | null): boolean {
    if (!start || !end) return false;

    return (
      start.getFullYear() === end.getFullYear() &&
      start.getMonth() === end.getMonth() &&
      start.getDate() === end.getDate()
    );
  }

  private parseTimeToMinutes(time: string | null): number | null {
    if (!time) return null;

    const [timePart, modifier] = time.trim().split(/\s+/);
    const [rawHour, minutes] = timePart.split(':').map(Number);

    let hours = rawHour;

    if (modifier?.toUpperCase() === 'PM' && hours !== 12) {
      hours += 12;
    }

    if (modifier?.toUpperCase() === 'AM' && hours === 12) {
      hours = 0;
    }

    return hours * 60 + minutes;
  }

  private validateRange(): boolean {
    this.validationMessage = null;

    if (!this.startDateObj) {
      this.validationMessage = 'Start date is required.';
      return false;
    }

    if (!this.isSingleDate && !this.endDateObj) {
      this.validationMessage = 'End date is required.';
      return false;
    }

    if (!this.isSingleDate && this.startDateObj && this.endDateObj && this.endDateObj < this.startDateObj) {
      this.validationMessage = 'End date cannot be before start date.';
      return false;
    }

    if (!this.isSingleDate && this.isSameDay(this.startDateObj, this.endDateObj)) {
      const startMinutes = this.parseTimeToMinutes(this.startTime);
      const endMinutes = this.parseTimeToMinutes(this.endTime);

      if (startMinutes !== null && endMinutes !== null && endMinutes <= startMinutes) {
        this.validationMessage = 'End time must be later than start time when start and end dates are the same.';
        return false;
      }
    }

    return true;
  }

  onStartTimeChange(): void {
    if (!this.startDateObj || !this.endDateObj) return;
    if (!this.isSameDay(this.startDateObj, this.endDateObj)) return;

    const startMinutes = this.parseTimeToMinutes(this.startTime);
    const endMinutes = this.parseTimeToMinutes(this.endTime);

    if (startMinutes !== null && endMinutes !== null && endMinutes <= startMinutes) {
      this.endTime = null;
    }
  }

  private combineDateAndTime(dateIso: string | null, time: string | null): string | null {
    if (!dateIso) return null;

    const date = this.parseFromIsoLocalClock(dateIso);

    let hours = 0;
    let minutes = 0;

    if (time) {
      const [timePart, modifier] = time.trim().split(/\s+/);
      const [h, m] = timePart.split(':').map(Number);

      hours = h;
      minutes = m;

      if (modifier) {
        const mod = modifier.toUpperCase();
        if (mod === 'PM' && hours !== 12) {
          hours += 12;
        }
        if (mod === 'AM' && hours === 12) {
          hours = 0;
        }
      }
    }

    date.setHours(hours, minutes, 0, 0);
    return this.parseToIsoLocalCLock(date);
  }

  private formatDisplayDate(date: Date): string {
    return new Intl.DateTimeFormat('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    }).format(date);
  }

  private parseFromIsoLocalClock(value: string): Date {
    const normalized = value.trim().replace(' ', 'T');
    const [datePart, timePartRaw] = normalized.split('T');

    const dateParts = datePart.split('-').map(Number);
    if (dateParts.length !== 3 || dateParts.some(Number.isNaN)) return new Date(value);

    const [y, m, d] = dateParts;

    let hh = 0;
    let mm = 0;
    let ss = 0;
    let ms = 0;

    if (timePartRaw) {
      const timePart = timePartRaw.endsWith('Z') ? timePartRaw.slice(0, -1) : timePartRaw;
      const [clockPart, msPart = '0'] = timePart.split('.');
      const [h = '0', min = '0', sec = '0'] = clockPart.split(':');

      hh = Number(h);
      mm = Number(min);
      ss = Number(sec);
      ms = Number(msPart.padEnd(3, '0').slice(0, 3));

      if ([hh, mm, ss, ms].some(Number.isNaN)) return new Date(value);
    }

    return new Date(y, m - 1, d, hh, mm, ss, ms);
  }

  private parseToIsoLocalCLock(d: Date): string {
    // Formats like: YYYY-MM-DDTHH:mm:ss.sssZ
    // BUT uses local components, not UTC components.
    const pad2 = (n: number) => String(n).padStart(2, '0');
    const pad3 = (n: number) => String(n).padStart(3, '0');

    const yyyy = d.getFullYear();
    const MM = pad2(d.getMonth() + 1);
    const dd = pad2(d.getDate());
    const hh = pad2(d.getHours());
    const mm = pad2(d.getMinutes());
    const ss = pad2(d.getSeconds());
    const ms = pad3(d.getMilliseconds());

    return `${yyyy}-${MM}-${dd}T${hh}:${mm}:${ss}.${ms}Z`;
  }
}
